import { validate } from "@/lib/agents/playbooks/validate"
import type { HarnessTools } from "../types"

const ISSUES = 40

function flowName(description: string) {
  return { type: "string", description }
}

function merge<T extends object>(base: T | undefined, patch: object | undefined) {
  if (!patch) return base
  const next = Object.fromEntries(Object.entries({ ...base, ...patch }).filter(([, v]) => v !== null && v !== undefined))
  return Object.keys(next).length ? next as T : undefined
}

// Every edit lands on the editor at once and validates the whole playbook: its issues go back with the result,
// which is what lets the model correct itself in the same turn
export function designerTools(playbook: Playbook, onChange: (playbook: Playbook) => void): HarnessTools {
  let current = playbook

  function commit(next: Playbook) {
    current = next
    onChange(next)
    const issues = validate(next)
    return { ok: true, issues: issues.slice(0, ISSUES), more: issues.length > ISSUES ? issues.length - ISSUES : undefined }
  }

  function flow(name: string) {
    const found = current.flows[name]
    if (!found) throw new Error(`There is no flow "${name}". The flows are: ${Object.keys(current.flows).join(", ")}`)
    return found
  }

  function putFlow(name: string, next: PlaybookFlow) {
    return commit({ ...current, flows: { ...current.flows, [name]: next } })
  }

  return {
    get_playbook: {
      description: "Returns the playbook as it is now, with the edits of this turn",
      run: () => current,
    },
    put_flow: {
      description: "Creates a flow, or replaces the whole of an existing one. The way to write many items at once",
      parameters: {
        type: "object",
        properties: {
          flow: flowName("The key of the flow, a slug. main is where the conversation starts"),
          title: { type: "string", description: "Human label of the flow. Omitted keeps the current one" },
          items: { type: "array", items: { type: "object" }, description: "PlaybookItem[], in order" },
        },
        required: ["flow", "items"],
      },
      run: ({ flow: name, title, items }) => putFlow(name, { title: title ?? current.flows[name]?.title, items }),
    },
    remove_flow: {
      description: "Removes a flow and every item in it. main cannot be removed",
      parameters: { type: "object", properties: { flow: flowName("The key of the flow") }, required: ["flow"] },
      run: ({ flow: name }) => {
        if (name === "main") throw new Error("main cannot be removed")
        flow(name)
        const { [name]: _, ...flows } = current.flows
        return commit({ ...current, flows: flows as Playbook["flows"] })
      },
    },
    put_item: {
      description: "Adds an item to a flow, or replaces the item with the same id. A new item goes at index, or at the end. A replaced one stays where it was unless index is given, which moves it",
      parameters: {
        type: "object",
        properties: {
          flow: flowName("The key of the flow"),
          item: { type: "object", description: "The whole PlaybookItem, id included" },
          index: { type: "number", description: "0 based position in the flow" },
        },
        required: ["flow", "item"],
      },
      run: ({ flow: name, item, index }) => {
        if (!item?.id) throw new Error("An item needs an id")
        const items = flow(name).items
        const existing = items.findIndex(i => i.id === item.id)
        const rest = items.filter(i => i.id !== item.id)
        rest.splice(index ?? (existing >= 0 ? existing : rest.length), 0, item)
        return putFlow(name, { ...flow(name), items: rest })
      },
    },
    remove_item: {
      description: "Removes an item from a flow",
      parameters: { type: "object", properties: { flow: flowName("The key of the flow"), item: { type: "string", description: "The id of the item" } }, required: ["flow", "item"] },
      run: ({ flow: name, item }) => {
        const items = flow(name).items
        if (!items.some(i => i.id === item)) throw new Error(`There is no item "${item}" in "${name}"`)
        return putFlow(name, { ...flow(name), items: items.filter(i => i.id !== item) })
      },
    },
    set_playbook: {
      description: "Changes the title, the config or the defaults of the playbook. config and defaults are merged over what is there, and a field set to null is removed",
      parameters: {
        type: "object",
        properties: { title: { type: "string" }, config: { type: "object", description: "PlaybookConfig" }, defaults: { type: "object", description: "Playbook.defaults" } },
      },
      run: ({ title, config, defaults }) => commit({ ...current, title: title ?? current.title, config: merge(current.config, config), defaults: merge(current.defaults, defaults) }),
    },
  }
}
